import * as React from 'react';
import {
  Box,
  Button,
  Chip,
  Skeleton,
  Typography,
} from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { BackArrowIcon, } from '../../../icons';
import { GlobalStyles } from '@mui/material';
import ThreeDotMenu from '../../../components/university-table/ThreeDotMenu';
import UniversitySummary from '../../../components/university-table/UniversitySummary';
import CommonTableFormat from '../../../components/common/CommonTableFormat';
import CommonFilterTab from '../../../components/common/CommonFilterTab';
import { useNotification } from '../../../hooks/useNotification';
import { fetchSpecializationListByUniversityId, fetchSpecializationsNameSuggestion } from '../../../api/specializationApi';
import CommonDataTable from '../../common/CommonDataTable';
import { formatMultiQueryParams, jsonParserFormatter } from '../../../utils/helperFunction';
import { useDispatch } from 'react-redux';
import { defaultPaginationValues, filterTypeOptions, studentStatusList } from '../../../utils/constant';
import { setFilterField } from '../../../containers/filter-container/reducer/filterSlice';
import { useSelector } from 'react-redux';

const UniversityVariantSpecializationListing = () => {
  const dispatch = useDispatch()
  const filterSlice = useSelector(state => state.filter?.filters)
  const LOCATION_FILTER = `filters:${window.location.pathname}`
  const [rows, setRows] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const navigate = useNavigate();
  const { id, courseId } = useParams()
  const [rowCount, setRowCount] = React.useState(0);
  const [searchFilter, setSearchFilter] = React.useState({})
  const [pageFilters, setPageFilters] = React.useState(defaultPaginationValues);
  const [isRender, setIsRender] = React.useState(false)
  const [specializationSuggestion, setSpecializationSuggestion] = React.useState([])
  const { showErrorNotification, showSuccessNotification } = useNotification()


  const handleFliterSliceChange = (values = {}, isReset = false) => {
    let isQuery = typeof values === "object" && Object.keys(values)?.length
    isQuery && dispatch(setFilterField({ pathname: LOCATION_FILTER, filters: JSON.stringify({ ...(isReset ? {} : pageFilters), ...defaultPaginationValues, ...values }) }))
  }

  const fetchDetails = () => {
    setLoading(true)
    const multiFilter = formatMultiQueryParams(filterSlice[LOCATION_FILTER])
    fetchSpecializationListByUniversityId({ universityId: id, setRows, filterData: { ...searchFilter, ...multiFilter, isMultiFilter: true, ...(courseId ? { specializationCourseId:courseId } : []) }, setRowCount })
      .catch(err => { console.log('err', err?.message); showErrorNotification(err?.message || "Something went wrong") })
      .finally(() => setLoading(false))
  }

  const handleChange = async (id, field, value) => {
    if (field === 'action') {
      if (value === 'View') {
        const specialisationId = rows.find(r => r.id === id)?.specialisationId || '';
        navigate(`/specialization-listing-management/specialization-details/${specialisationId}`, {
          state: { viewOnly: true, source: 'ActionMenu' }
        });
        return;
      }
    }

    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  function getStatusClass(status) {
    switch (status) {
      case 'Live':
        return 'status-live';
      case 'Draft':
        return 'status-draft';
      case 'Pending':
        return 'status-pending';
      case 'Deactivated':
        return 'status-deactivated';
      default:
        return '';
    }
  }


  function getStatusBgColor(status) {
    switch (status) {
      case 'Live':
        return '#fff';
      case 'Draft':
        return '#fff';
      case 'Pending':
        return '#fff';
      case 'Deactivated':
        return '#fff';
      default:
        return '#fff';
    }
  }

  function getStatusTextColor(status) {
    switch (status) {
      case 'Live':
        return '#363853'; // darker green
      case 'Draft':
        return '#363853'; // darker gray
      case 'Pending':
        return '#363853'; // darker orange
      case 'Deactivated':
        return '#363853'; // darker red
      default:
        return '#666';
    }
  }

  // Helper functions for status colors
  function getStatusColor(status) {
    switch (status) {
      case 'Live':
        return '#22c55e'; // green
      case 'Draft':
        return '#64748b'; // grayish
      case 'Pending':
        return '#f97316'; // orange
      case 'Deactivated':
        return '#ef4444'; // red
      default:
        return '#999';
    }
  }

  const columns = [
    {
      field: 'sNo',
      headerName: 'S. No.',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
    },
    {
      field: 'specialisationId',
      headerName: 'Specialisation ID',
      flex: 1,
      minWidth: 130,
      cellClassName: 'sticky-col-1',
      headerClassName: 'sticky-col-1',
      filterColumnName: "specialisationId",
      filterType: filterTypeOptions.string,
    },
    {
      field: 'specialization',
      headerName: 'Variant Specialization',
      flex: 1.2,
      minWidth: 160,
      cellClassName: 'fw-bold',
      filterColumnName: "specializationNameId.name",
      filterType: filterTypeOptions.selection,
      selection: specializationSuggestion,
    },
    {
      field: 'course',
      headerName: 'Course',
      flex: 1.2,
      minWidth: 160,
      cellClassName: 'fw-bold',
      filterable: false,
    },
    {
      field: 'university',
      headerName: 'University',
      flex: 2,
      minWidth: 260,
      filterable: false,
      renderCell: (params) => {
        const { universityLogo, university, universityId } = params.row;
        const displayName = university;

        const getInitials = (fullName) => {
          if (!fullName) return '';
          const words = fullName.trim().split(' ').filter(Boolean);
          if (words.length === 1) return words[0][0]?.toUpperCase() || '';
          if (words.length === 2) return (words[0][0] + words[1][0]).toUpperCase();
          return (words[0][0] + words[1][0] + words[2][0]).toUpperCase();
        };

        const stringToColor = (str) => {
          let hash = 0;
          for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
          }
          const c = (hash & 0x00ffffff).toString(16).toUpperCase();
          return '#' + '00000'.substring(0, 6 - c.length) + c;
        };

        const bgColor = stringToColor(displayName || 'University');

        return loading ? (
          <Skeleton width={150} />
        ) : (
          <Box display="flex" alignItems="center" gap={1} sx={{ height: '100%' }}>
            {universityLogo ? (
              <Box
                component="img"
                src={universityLogo}
                alt="universityLogo"
                sx={{
                  width: 40,
                  height: 40,
                  padding: '5px',
                  borderRadius: '50%',
                  objectFit: 'contain',
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                  display: 'block',
                  maxHeight: 60,
                }}
              />
            ) : (
              <Box
                sx={{
                  minWidth: 35,
                  minHeight: 35,
                  maxWidth: 35,
                  maxHeight: 35,
                  borderRadius: '50%',
                  backgroundColor: bgColor,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 500,
                  fontSize: '10px',
                  textTransform: 'uppercase',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                }}
              >
                {getInitials(displayName)}
              </Box>
            )}
            <Box>
              <Typography sx={{ fontSize: '13px !important', fontWeight: 500 }}>
                {displayName}
              </Typography>
              {params.row.universityNote && (
                <Typography sx={{ fontSize: '11px !important', fontWeight: '500', color: '#363853' }}>
                  ({params.row.universityNote})
                </Typography>
              )}
              <Typography
                sx={{ fontSize: '11px !important', fontWeight: '500', color: '#6E7485', paddingTop: '4px' }}
              >
                University ID: {universityId}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 130,
      filterColumnName: "status",
      filterType: filterTypeOptions.selection,
      selection: studentStatusList,
      renderCell: (params) =>
        loading ? (
          <Skeleton width={70} />
        ) : (
          <Chip
            label={params.value}
            size="small"
            icon={
              <FiberManualRecordIcon
                className={getStatusClass(params.value)}
                sx={{
                  fontSize: 10,
                  color: getStatusColor(params.value),
                }}
              />
            }
            sx={{
              bgcolor: getStatusBgColor(params.value),
              color: getStatusTextColor(params.value),
              fontWeight: 500,
            }}
          />
        ),
    },
    {
      field: 'createdDate',
      headerName: 'Created Date',
      flex: 1,
      minWidth: 130,
      filterColumnName: "createdAt",
      filterType: filterTypeOptions.dateRange,
    },
    {
      field: 'lastUpdate',
      headerName: 'Last Update',
      flex: 1,
      minWidth: 130,
      filterColumnName: "updatedAt",
      filterType: filterTypeOptions.dateRange,
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 100,
      filterable: false,
      renderCell: (params) =>
        loading ? <Skeleton width={80} /> : <ThreeDotMenu rowId={params.row.id} onAction={handleChange} />,
    },
  ];

  // initial load or when filter slice change
  React.useEffect(() => {
    fetchDetails()
  }, [filterSlice[LOCATION_FILTER], isRender])

  // handle extra filter tabs changes
  React.useEffect(() => {
    if (pageFilters?.page) {
      handleFliterSliceChange({ page: 0 })
    } else {
      setIsRender(!isRender)
    }
  }, [searchFilter])

  // parse page filters
  React.useEffect(() => {
    const values = jsonParserFormatter(filterSlice[LOCATION_FILTER])
    setPageFilters(values)
  }, [filterSlice[LOCATION_FILTER]])

  // load  for filter suggestions
  React.useEffect(() => {
    fetchSpecializationsNameSuggestion((result) => { setSpecializationSuggestion(result?.map(el => ({ ...el, value: encodeURIComponent(el?.label) }))) }, { skipLimit: true })
      .catch(() => { })
  }, [])


  return (
    <>
      {/* Apply global styles to DataGrid dropdown menus */}

      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div className="page-header" style={{ position: 'sticky', top: '4.5rem', zIndex: '9', padding: '10px 15px', borderBottom: '1px solid #E6E7E8', background: '#fff', }}>
          <div className="row align-items-center">
            <div className="col">
              <Typography sx={{ fontSize: '18px', fontWeight: '600', color: '#15243E', display: 'flex', alignItems: 'center', gap: 1, }}><Link to={-1} className='d-flex'><BackArrowIcon /></Link>University Specialization Listing Management  </Typography>
            </div>
          </div>
          {/* End Col */}
        </div>
        <UniversitySummary type="course" isAdmin />
        {/* Filter Toolbar */}
        <CommonFilterTab type='university-course' module='admin' searchPlaceholder='Search specialization name...' detailType='University Specialization Details' fetchList={setSearchFilter} showCount={rows?.length} rowCount={rowCount} />

        {/* Data Table */}
        <CommonDataTable columns={columns} rows={rows} idKey="field" loading={loading} filterEnabled={true} rowCount={rowCount}
          handleFilterApply={() => { }}
          pageFilters={pageFilters}
          handleFliterSliceChange={handleFliterSliceChange}
        />

      </Box>
    </>
  )
}

export default UniversityVariantSpecializationListing
